import { 
  DonneesStructureBasePourPortes, 
  ElementPorte, 
  ParametresPorte, 
  ParametresPortes, 
  Point3D, 
  Position3D, 
  StructurePortes
} from '../types';
/**
 * Service de génération 3D pour les Portes - Version simplifiée
 * - Place automatiquement les portes selon le nombre de répétitions
 * - Utilise matériau/couleur/épaisseur de la structure de base
 * - Support géométrie arrondie pour le haut des portes
 */
export class Portes3DService {

  genererPortes(parametres: ParametresPortes, donnees: DonneesStructureBasePourPortes): StructurePortes {
    const now = Date.now();
    const portesActives = (parametres.portes || []).filter(p => p.actif);
    const elements: ElementPorte[] = [];

    for (const p of portesActives) {
      const elementsPorte = this.genererElementsPorte(p, donnees);
      elements.push(...elementsPorte);
    }

    const structure: StructurePortes = {
      id: `portes-${now}`,
      timestamp: now,
      parametres,
      elements,
      statistiques: {
        nombrePortes: elements.length,
        surfaceTotale: elements.reduce((acc, el) => acc + el.geometrie.surface, 0),
        poidesEstime: this.calculerPoidsEstime(elements, donnees)
      }
    };

    return structure;
  }

  private genererElementsPorte(parametres: ParametresPorte, donnees: DonneesStructureBasePourPortes): ElementPorte[] {
    const elements: ElementPorte[] = [];
    const placements = this.calculerPlacementsSurPerimetre(parametres, donnees);
    placements.forEach(({ position, tangent, normal, leftBase, rightBase, baseCurve }, index) => {
      const element = this.creerElementPorte(parametres, donnees, position, index, tangent, normal, leftBase, rightBase, baseCurve);
      if (element) elements.push(element);
    });
    return elements;
  }

  // Nouvelle logique: répartition uniforme sur TOUT le périmètre
  private calculerPlacementsSurPerimetre(parametres: ParametresPorte, donnees: DonneesStructureBasePourPortes): { position: Position3D; tangent: Point3D; normal: Point3D; leftBase: Point3D; rightBase: Point3D; baseCurve: { x: number; y: number; z: number; n: { x: number; z: number } }[] }[] {
    const { nombreRepetitions, largeur, hauteur } = parametres;
    if (!nombreRepetitions || nombreRepetitions <= 0) return [];

    const { centre } = donnees;

    const epsilon = 0.002; // éviter z-fighting

    // Obtenir un chemin de périmètre (priorité poutreBasse, sinon poutreHaute, sinon forme analytique)
    const chemin = this.obtenirCheminPerimetre(donnees);
    if (chemin.length < 2) {
      return [];
    }

    // Assurer la fermeture du chemin
    const points: Point3D[] = [...chemin];
    const first = points[0];
    const last = points[points.length - 1];
    const distClose = Math.hypot(last.x - first.x, last.z - first.z);
    if (distClose > 1e-6) {
      points.push({ x: first.x, y: first.y, z: first.z });
    }

    // Calcul des longueurs cumulées du polyline 2D (x,z)
    const segLengths: number[] = [];
    const cumLengths: number[] = [0];
    let total = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const dx = points[i+1].x - points[i].x;
      const dz = points[i+1].z - points[i].z;
      const len = Math.hypot(dx, dz);
      segLengths.push(len);
      total += len;
      cumLengths.push(total);
    }
    if (total <= 1e-6) return [];

    // Positions d’arc des sommets (sans le dernier point de fermeture)
    const sommetsS: number[] = cumLengths.slice(0, cumLengths.length - 1);
    const step = total / nombreRepetitions;
    const margeSommet = (largeur / 2) + 1e-3; // éviter de croiser un angle

    // Helper: normalise un vecteur 2D (xz)
    const norm2 = (x: number, z: number) => {
      const l = Math.hypot(x, z) || 1;
      return { x: x / l, z: z / l };
    };

    // Helper: distance circulaire minimale à un sommet
    const minDistToVertices = (s: number) => {
      let min = Infinity;
      for (const sv of sommetsS) {
        const d = Math.abs(s - sv);
        min = Math.min(min, Math.min(d, total - d));
      }
      return min;
    };

    // Helper: récupérer point (x,z), y local et tangente pour une abscisse curviligne s
    const sampleAt = (s: number) => {
      // s dans [0,total)
      s = ((s % total) + total) % total;
      // trouver segment
      let segIndex = 0;
      while (segIndex < segLengths.length && cumLengths[segIndex + 1] < s) segIndex++;
      const segStartLen = cumLengths[segIndex];
      const lseg = segLengths[segIndex] || 1;
      const localT = (s - segStartLen) / lseg;
      const p0 = points[segIndex];
      const p1 = points[segIndex + 1];
      const x = p0.x + (p1.x - p0.x) * localT;
      const z = p0.z + (p1.z - p0.z) * localT;
      const y = p0.y + (p1.y - p0.y) * localT;
      const prev = points[(segIndex - 1 + (points.length - 1)) % (points.length - 1)];
      const next = points[(segIndex + 1) % (points.length - 1)];
      const tx = next.x - prev.x;
      const tz = next.z - prev.z;
      const t = norm2(tx, tz); // tangente unitaire
      // normale du plan de la porte = t x up (up=(0,1,0)) => ( -t.z, 0, t.x )
      let nx = -t.z;
      let nz = t.x;
      // orienter vers l’extérieur basé sur centre
      const ox = x - centre.x;
      const oz = z - centre.z;
      if ((nx * ox + nz * oz) < 0) { nx = -nx; nz = -nz; }
      return { x, z, y, t, n: { x: nx, z: nz } };
    };

    const placements: { position: Position3D; tangent: Point3D; normal: Point3D; leftBase: Point3D; rightBase: Point3D; baseCurve: { x: number; y: number; z: number; n: { x: number; z: number } }[] }[] = [];

    for (let k = 0; k < nombreRepetitions; k++) {
      // Échantillonnage centré pour éviter les angles et assurer la symétrie
      let s = (k + 0.5) * step;
      s = ((s % total) + total) % total;

      // Éviter d’être trop proche des sommets (au moins largeur/2)
      let attempts = 0;
      let dmin = minDistToVertices(s);
      while (dmin < margeSommet && attempts < segLengths.length * 2) {
        const delta = (margeSommet - dmin) + 1e-4;
        s = s + delta; // pousser vers l’avant le long du périmètre
        if (s >= total) s -= total;
        dmin = minDistToVertices(s);
        attempts++;
      }

      const center = sampleAt(s);
      const sLeft = s - largeur / 2;
      const sRight = s + largeur / 2;
      const left = sampleAt(sLeft);
      const right = sampleAt(sRight);

      // Position 3D avec léger décalage le long de la normale pour éviter z-fighting
      const px = center.x + center.n.x * epsilon;
      const pz = center.z + center.n.z * epsilon;
      const yPorte = center.y + (hauteur / 2);

      // Courbe de base entre gauche et droite (suivre exactement la poutre)
      const segments = 16; // finesse de l’approximation
      const baseCurve: { x: number; y: number; z: number; n: { x: number; z: number } }[] = [];
      for (let i = 0; i <= segments; i++) {
        const si = sLeft + (i / segments) * (sRight - sLeft);
        const pt = sampleAt(si);
        baseCurve.push({ x: pt.x, y: pt.y, z: pt.z, n: { x: pt.n.x, z: pt.n.z } });
      }

      placements.push({
        position: { x: px, y: yPorte, z: pz },
        tangent: { x: center.t.x, y: 0, z: center.t.z },
        normal: { x: center.n.x, y: 0, z: center.n.z },
        leftBase: { x: left.x, y: left.y, z: left.z },
        rightBase: { x: right.x, y: right.y, z: right.z },
        baseCurve
      });
    }

    return placements;
  }

  private obtenirCheminPerimetre(donnees: DonneesStructureBasePourPortes): Point3D[] {
    // 1) Utiliser les points de poutre basse si disponibles
    if (donnees.poutreBasse && Array.isArray(donnees.poutreBasse.points) && donnees.poutreBasse.points.length >= 2) {
      return donnees.poutreBasse.points.map(p => ({ x: p.x, y: p.y, z: p.z }));
    }
    // 2) Sinon, poutre haute
    if (donnees.poutreHaute && Array.isArray(donnees.poutreHaute.points) && donnees.poutreHaute.points.length >= 2) {
      return donnees.poutreHaute.points.map(p => ({ x: p.x, y: p.y, z: p.z }));
    }

    // 3) Sinon, reconstruire un périmètre analytique selon la forme et dimensions
    const { forme, largeur, longueur = largeur, centre } = donnees as any;
    const pts: Point3D[] = [];
    const demiL = largeur / 2;
    const demiLo = longueur / 2;

    switch ((forme || '').toString().toLowerCase()) {
      case 'carre':
      case 'rectangle': {
        pts.push(
          { x: centre.x - demiL, y: centre.y, z: centre.z - demiLo },
          { x: centre.x + demiL, y: centre.y, z: centre.z - demiLo },
          { x: centre.x + demiL, y: centre.y, z: centre.z + demiLo },
          { x: centre.x - demiL, y: centre.y, z: centre.z + demiLo }
        );
        return pts;
      }
      case 'cercle':
      case 'ovale': {
        const n = 128; // points pour approximer
        const rx = largeur / 2;
        const rz = (forme.toLowerCase() === 'cercle' ? largeur : longueur) / 2;
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2;
          pts.push({ x: centre.x + rx * Math.cos(a), y: centre.y, z: centre.z + rz * Math.sin(a) });
        }
        return pts;
      }
      default: {
        // Fallback: rectangle
        pts.push(
          { x: centre.x - demiL, y: centre.y, z: centre.z - demiLo },
          { x: centre.x + demiL, y: centre.y, z: centre.z - demiLo },
          { x: centre.x + demiL, y: centre.y, z: centre.z + demiLo },
          { x: centre.x - demiL, y: centre.y, z: centre.z + demiLo }
        );
        return pts;
      }
    }
  }

  private creerElementPorte(parametres: ParametresPorte, donnees: DonneesStructureBasePourPortes, position: Position3D, index: number, tangent: Point3D, normal: Point3D, leftBase: Point3D, rightBase: Point3D, baseCurve: { x: number; y: number; z: number; n: { x: number; z: number } }[]): ElementPorte | null {
    if (parametres.hauteur > donnees.hauteur) {
      console.warn(`Porte ${parametres.id}: hauteur trop grande pour la structure`);
      return null;
    }

    const coins = this.creerFormePorte(position, parametres, tangent, normal, leftBase, rightBase, baseCurve);
    const centre = this.calculerCentre(coins);
    const normale = { x: normal.x, y: 0, z: normal.z };
    const surface = parametres.largeur * parametres.hauteur;

    const vertices = this.coinsToVertices(coins);
    const indices = this.genererIndices(coins.length, parametres.formeArrondie);

    const materiaux = {
      diffuse: donnees.couleur || '#8B4513',
      ambient: '#202020',
      specular: '#aaaaaa',
      shininess: 8,
      transparency: 0
    };

    return {
      id: `${parametres.id}-${index}`,
      nom: `${parametres.nom} ${index + 1}`,
      geometrie: { 
        coins, 
        centre, 
        normale, 
        surface,
        formeArrondie: parametres.formeArrondie 
      },
      parametres,
      vertices,
      indices,
      materiaux
    };
  }

  private creerFormePorte(position: Position3D, parametres: ParametresPorte, tangent: Point3D, normal: Point3D, leftBase: Point3D, rightBase: Point3D, baseCurve: { x: number; y: number; z: number; n: { x: number; z: number } }[]): Point3D[] {
    const { hauteur } = parametres;
    const epsilon = 0.002;
    // Suivre exactement la poutre basse: on extrude verticalement la courbe de base
    const bas: Point3D[] = baseCurve.map(p => ({ x: p.x + p.n.x * epsilon, y: p.y, z: p.z + p.n.z * epsilon }));
    const haut: Point3D[] = baseCurve.map(p => ({ x: p.x + p.n.x * epsilon, y: p.y + hauteur, z: p.z + p.n.z * epsilon }));
    return [...bas, ...haut];
  }

  private genererIndices(nombrePoints: number, formeArrondie: boolean): number[] {
    // Si on a un ruban (bas/top), nombrePoints est pair >= 6
    if (nombrePoints % 2 === 0 && nombrePoints >= 6) {
      const ring = nombrePoints / 2; // nombre de points par anneau
      const indices: number[] = [];
      for (let i = 0; i < ring - 1; i++) {
        const a = i;
        const b = i + 1;
        const c = ring + i;
        const d = ring + i + 1;
        // Deux triangles par quad: a-b-d et a-d-c
        indices.push(a, b, d);
        indices.push(a, d, c);
      }
      return indices;
    }
  
    // Cas ancien: polygone plat
    if (!formeArrondie) {
      return [0, 1, 2, 2, 3, 0];
    } else {
      const indices: number[] = [];
      for (let i = 1; i < nombrePoints - 1; i++) {
        indices.push(0, i, i + 1);
      }
      return indices;
    }
  }

  private calculerCentre(points: Point3D[]): Point3D {
    const cx = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const cy = points.reduce((sum, p) => sum + p.y, 0) / points.length;
    const cz = points.reduce((sum, p) => sum + p.z, 0) / points.length;
    return { x: cx, y: cy, z: cz };
  }

  private coinsToVertices(points: Point3D[]): number[] {
    const v: number[] = [];
    for (const p of points) {
      v.push(p.x, p.y, p.z);
    }
    return v;
  }

  private calculerPoidsEstime(elements: ElementPorte[], donnees: DonneesStructureBasePourPortes): number {
    // Estimation basée sur l'épaisseur de la structure de base
    const epaisseurM = (donnees.epaisseur || 40) / 1000; // mm vers m
    const densiteKgM3 = 7850; // Densité approximative pour métal
    
    return elements.reduce((acc, el) => {
      return acc + (el.geometrie.surface * epaisseurM * densiteKgM3);
    }, 0);
  }

  dispose(): void {
    // Nettoyage des ressources si nécessaire
  }
}
/* Ancienne version simple de genererIndices supprimée pour éviter le doublon. Conserver uniquement la version ruban plus bas. */